import { IMinMaxDefault, IWeightTargetInfo, TargetStatus, UserTargetTypes, WeightTargetStep, ITargetInfo } from 'fitbeat.models';
import { USER_GOAL_CHANGED } from '../SummaryDashboard/actionTypes';
import { LOGOUT, makeDates } from './../utils';
import {
    INITIAL_TARGET_SET_DONE,
    LOAD_CURRENT_TARGET_INFO,
    LOADED_CURRENT_TARGET_INFO,
    REQUEST_WEIGHT_TARGET_CALC, REQUEST_WEIGHT_TARGET_CALC_SUCCESS, REQUEST_WEIGHT_TARGET_INFO,
    REQUEST_WEIGHT_TARGET_INFO_SUCCESS, REQUEST_WEIGHT_TARGET_RESET, REQUEST_WEIGHT_TARGET_RESET_SUCCESS,
    REQUEST_WEIGHT_TARGET_SAVE, REQUEST_WEIGHT_TARGET_SAVE_FAIL, REQUEST_WEIGHT_TARGET_SAVE_SUCCESS,
    SET_HAS_SCALE, SET_PAL_VALUE, SET_TARGET_WEIGHT, SET_WEIGHT_TARGET_STEP,
    UPDATE_MILESTONE_REQUEST, UPDATE_MILESTONE_REQUEST_SUCCESS, LOAD_CHANGE_TARGET_SCREEN_SUCCESS,
    LOAD_CHANGE_TARGET_SCREEN_FAILURE, LOAD_CHANGE_TARGET_SCREEN_REQUEST, CLOSE_TARGET_ADJUST_SCREEN, REMOVE_TARGET_SUCCESS, REMOVE_TARGET_REQUEST, REMOVE_TARGET_FAILURE,
} from './actionTypes';
import { LOAD_TRACKING_DATA_FOR_DASHBOARD } from '../Tracking/actionTypes';

interface IWeightTargetView {
    step: WeightTargetStep | null;
    isApplyingTarget: boolean;
    error: string | null;
    targetStatus: TargetStatus;
    userTargetTypeForScreens: UserTargetTypes | null;
    isProcessingWeightReview: boolean;
    initialTargetSet: boolean;
    isLoadingTarget: boolean;
    isCalculatingTarget: boolean;
    isResettingTarget: boolean;
    hasScale: boolean;
    isChangingTarget: boolean;
    isRemovingTarget: boolean;
}

interface IWeightTargetData {
    reviewedWeight: number | null;
    targetWeight: number | null;
    minMaxDefault: IMinMaxDefault | null;
    weightTargetInfo: IWeightTargetInfo | null;
    targetAdjustment: IWeightTargetInfo | null;
    bodyWeightGraphData: any;
    PALValue: number|null;
}

export interface IWeightTargetState {
    view: IWeightTargetView;
    data: IWeightTargetData;
}

const initialState: IWeightTargetState = {
    view: {
        step: null,
        isApplyingTarget: false,
        error: null,
        targetStatus: TargetStatus.NotReached,
        isProcessingWeightReview: false,
        initialTargetSet: false,
        userTargetTypeForScreens: null,
        isLoadingTarget: false,
        isCalculatingTarget: false,
        isResettingTarget: false,
        hasScale: null,
        isChangingTarget: false,
    },
    data: {
        reviewedWeight: null,
        targetWeight: null,
        minMaxDefault: {},
        weightTargetInfo: {},
        targetAdjustment: {},
        bodyWeightGraphData: null,
        PALValue: null,
    },
};

export default (
    state = initialState,
    action: { type: any; payload: any; }) => {
    switch (action.type) {
        case SET_WEIGHT_TARGET_STEP: {
            const step = action.payload.step;

            return {
                view: {
                    ...state.view,
                    step,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_INFO: {
            const reviewedWeight = action.payload.reviewedWeight;

            return {
                view: {
                    ...state.view,
                    isProcessingWeightReview: action.payload.hideLoader ? false : true,
                },
                data: {
                    ...state.data,
                    reviewedWeight,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_INFO_SUCCESS: {
            const weightTargetInfo = action.payload.weightTargetInfo;
            const userTargetType = action.payload.userTargetType;

            const targetWeight = !weightTargetInfo.target ?
                state.data.minMaxDefault.default : weightTargetInfo.target.weight;
            let step = WeightTargetStep.BodyWeightAdjust;
            const bodyWeightGraphData = action.payload.bodyMetricSet
                && makeDates(action.payload.bodyMetricSet)[0];

            let targetStatus = TargetStatus.NotReached;
            if (weightTargetInfo.milestone && weightTargetInfo.milestone.reached) {
                targetStatus = TargetStatus.MilestoneReached;
            }
            if (weightTargetInfo.target) {
                step = WeightTargetStep.BodyWeightSummary;
                if (weightTargetInfo.target.reached) {
                    targetStatus = TargetStatus.TargetReached;
                }
            }

            return {
                view: {
                    ...state.view,
                    isProcessingWeightReview: false,
                    step,
                    targetStatus,
                    userTargetTypeForScreens: userTargetType,
                },
                data: {
                    ...state.data,
                    targetWeight,
                    weightTargetInfo,
                    bodyWeightGraphData,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_CALC: {
            return {
                view: {
                    ...state.view,
                    isProcessingWeightReview: true,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_CALC_SUCCESS: {
            const target = action.payload.weightTarget;
            const minMaxDefault = action.payload.minMaxDefault;
            const userTargetTypeForScreens = action.payload.userTargetType;

            let targetWeight = state.data.targetWeight;
            if (!targetWeight && minMaxDefault) {
                targetWeight = target.weight ? target.weight : minMaxDefault.default;
            }

            return {
                view: {
                    ...state.view,
                    isProcessingWeightReview: false,
                    isCalculatingTarget: false,
                    userTargetTypeForScreens,
                },
                data: {
                    ...state.data,
                    ...(minMaxDefault && { minMaxDefault }),
                    targetWeight,
                    targetAdjustment: {
                        ...state.data.targetAdjustment,
                        target,
                    },
                },
            };
        }
        case USER_GOAL_CHANGED: {
            return {
                view: {
                    ...state.view,
                },
                data: {
                    ...state.data,
                    weightTargetInfo: {
                        target: undefined,
                        milestone: undefined,
                    },
                },
            };
        }
        case SET_TARGET_WEIGHT: {
            const targetWeight = Math.round(action.payload.targetWeight * 100) / 100;
            const isCalculatingTarget = action.payload.calculating;

            return {
                view: {
                    ...state.view,
                    isCalculatingTarget,
                },
                data: {
                    ...state.data,
                    targetWeight,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_SAVE: {
            return {
                view: {
                    ...state.view,
                    isApplyingTarget: true,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_SAVE_SUCCESS: {
            return {
                view: {
                    ...state.view,
                    targetStatus: TargetStatus.TargetSet,
                    initialTargetSet: true,
                    isApplyingTarget: false,
                    weightTargetInfo: {...state.data.targetAdjustment, ...state.data.weightTargetInfo},
                    targetAdjustment: {},
                    isChangingTarget: false,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_SAVE_FAIL: {
            const error = action.payload.error;
            return {
                view: {
                    ...state.view,
                    targetStatus: TargetStatus.NotReached,
                    initialTargetSet: false,
                    isApplyingTarget: false,
                    error,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case INITIAL_TARGET_SET_DONE: {
            return {
                view: {
                    ...state.view,
                    targetStatus: TargetStatus.NotReached,
                    initialTargetSet: false,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case LOAD_CURRENT_TARGET_INFO: {
            return {
                view: {
                    ...state.view,
                    isLoadingTarget: true,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case LOADED_CURRENT_TARGET_INFO: {
            const minMaxDefault = action.payload.minMaxDefault;
            const userTargetType = action.payload.userTargetType;

            return {
                view: {
                    ...state.view,
                    isLoadingTarget: false,
                    step: WeightTargetStep.BodyWeightAdjust,
                    userTargetTypeForScreens: userTargetType,
                },
                data: {
                    ...state.data,
                    minMaxDefault,
                    targetAdjustment: state.data.weightTargetInfo,
                },
            };
        }
        case LOAD_CHANGE_TARGET_SCREEN_REQUEST: {
          return {
            view: {
              ...state.view,
              isChangingTarget: true,
              step: WeightTargetStep.BodyWeightAdjust,
            },
            data: {
              ...state.data,
            },
          };
        }
        case LOAD_CHANGE_TARGET_SCREEN_SUCCESS: {
          const minMaxDefault = action.payload.minMaxDefault;
          const targetInfo = action.payload.target;
          const scaleScanStats = action.payload.bodyScaleScanStats;

          return {
            view: {
              ...state.view,
              userTargetTypeForScreens: targetInfo.target.targetType,
              isChangingTarget: true,
            },
            data: {
              ...state.data,
              minMaxDefault,
              targetAdjustment: targetInfo,
              reviewedWeight: scaleScanStats.weight,
              weightTargetInfo: targetInfo,
            },
          };
        }
        case LOAD_CHANGE_TARGET_SCREEN_FAILURE: {
          return {
            view: {
              ...state.view,
              isChangingTarget: false,
            },
            data: {
              ...state.data,
            },
          };
        }
        case REQUEST_WEIGHT_TARGET_RESET: {
            return {
                view: {
                    ...state.view,
                    isResettingTarget: true,
                },
                data: {
                    ...state.data,
                    minMaxDefault: {},
                    targetWeight: null,
                },
            };
        }
        case UPDATE_MILESTONE_REQUEST: {
            return {
                view: {
                    ...state.view,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case REQUEST_WEIGHT_TARGET_RESET_SUCCESS: {
            const weightTargetInfo = action.payload.weightTargetInfo;

            return {
                view: {
                    ...state.view,
                    isResettingTarget: false,
                },
                data: {
                    ...state.data,
                    weightTargetInfo,
                    targetAdjustment: weightTargetInfo,
                },
            };
        }
        case UPDATE_MILESTONE_REQUEST_SUCCESS: {
            return {
                view: {
                    ...state.view,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case LOGOUT: {
            return {
                view: {
                    ...state.view,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case SET_PAL_VALUE: {
            return {
                view: {
                    ...state.view,
                },
                data: {
                    ...state.data,
                    PALValue: action.payload.PALValue,
                },
            };
        }
        case SET_HAS_SCALE: {
            return {
                view: {
                    ...state.view,
                    hasScale : action.payload.hasScale,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case CLOSE_TARGET_ADJUST_SCREEN: {
            return {
                view: {
                    ...state.view,
                    isChangingTarget: false,
                },
                data: {
                    ...state.data,
                },
            };
        }
        case LOAD_TRACKING_DATA_FOR_DASHBOARD: {
          const target = action.payload.target;
          return {
            view: {
              ...state.view,
            },
            data: {
              ...state.data,
              weightTargetInfo: target,
            },
          };
        }
        case REMOVE_TARGET_REQUEST: {
          return {
            view: {
              ...state.view,
              isRemovingTarget: true,
            },
            data: {
                ...state.data,
            },
          };
        }
        case REMOVE_TARGET_SUCCESS: {
          const target = action.payload.target;
          return {
            view: {
              ...state.view,
              isRemovingTarget: false,
            },
            data: {
              ...state.data,
              weightTargetInfo: target,
            },
          };
        }
        case REMOVE_TARGET_FAILURE: {
          return {
            view: {
              ...state.view,
              isRemovingTarget: false,
            },
            data: {
                ...state.data,
            },
          };
        }

        default:
            return state;
    }
};
